import React, { useState } from 'react';
import { Facebook, Instagram, Copy, Check } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Reusable share button set for jamboree flyers.
 * - Facebook: opens sharer.php in a popup (uses OG tags from <head>)
 * - Instagram: tries native Web Share API on mobile; falls back to copy-link
 *   (with inline 'Link copied!' feedback) — IG has no direct web share URL.
 */
const SHARE_GROUP_STYLE = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '0.6rem',
  marginTop: '1rem',
};

const FB_BTN_STYLE = {
  background: '#1877F2',
  color: '#fff',
  fontWeight: 700,
  border: 'none',
};

const IG_BTN_STYLE = {
  background: 'linear-gradient(45deg, #F58529 0%, #DD2A7B 50%, #8134AF 100%)',
  color: '#fff',
  fontWeight: 700,
  border: 'none',
};

const COPY_BTN_STYLE = {
  background: 'transparent',
  border: '1px solid rgba(255, 215, 0, 0.5)',
  color: '#FFD700',
  fontWeight: 700,
};

const SocialShareButtons = ({ shareTitle, testIdPrefix = 'share' }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/jamboree`
      : '/jamboree';

  const openFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=620,height=520');
  };

  const shareInstagram = async () => {
    // IG has no direct web share URL. On mobile, native share sheet includes IG.
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl });
        return;
      } catch {
        // user cancelled — fall through to copy
      }
    }
    await copyLink();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      /* user cancelled or clipboard unavailable — no-op */
    }
  };

  return (
    <div style={SHARE_GROUP_STYLE} data-testid={`${testIdPrefix}-buttons`}>
      <Button
        onClick={openFacebook}
        style={FB_BTN_STYLE}
        data-testid={`${testIdPrefix}-facebook-btn`}
      >
        <Facebook size={18} />
        &nbsp;Share to Facebook
      </Button>

      <Button
        onClick={shareInstagram}
        style={IG_BTN_STYLE}
        data-testid={`${testIdPrefix}-instagram-btn`}
      >
        <Instagram size={18} />
        &nbsp;Share to Instagram
      </Button>

      <Button
        onClick={copyLink}
        style={COPY_BTN_STYLE}
        data-testid={`${testIdPrefix}-copy-link-btn`}
      >
        {copied ? (
          <>
            <Check size={18} />
            &nbsp;Link Copied!
          </>
        ) : (
          <>
            <Copy size={18} />
            &nbsp;Copy Link
          </>
        )}
      </Button>
    </div>
  );
};

export default SocialShareButtons;
